import React, { useContext } from "react";

import { Autocomplete, TextField, Typography, Stack, Box, Tooltip, Popper } from "@mui/material";
import { Info } from '@mui/icons-material';

import { SettingsOptionsContext } from "../SettingsOptions";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";

export type OptionsValueType = {
  label: string;
  value: string | number;
  group?: string;
  tooltip?: string;
};

interface SettingsSingleSelectProps {
  /**
   * Optional - If "value" prop is null, this is next in line as the inital state value.
   */
  defaultValue: OptionsValueType;
  /**
   * Acts as the identifier of the settings option.
   */
  keyid: string;
  /**
   * Label to be displayed in the UI
   */
  label: string;
  /**
   * Determines the position of the label in relation to the select component. Defaults to "left".
   */
  labelPosition?: "top" | "left";
  /**
   * Selectable options to display
   * @example [{ label: 'Center', value: 'center' }]
   */
  options: OptionsValueType[];
  /**
   * Optional - Allows component to act as either controlled or uncontrolled
   */
  value?: OptionsValueType;
}

export default function SettingsSingleSelect(props: SettingsSingleSelectProps) {
  const settingsOptions = useContext(SettingsOptionsContext);
  const isLabelOnTop = props.labelPosition === "top";

  return (
    <Stack
      direction={isLabelOnTop ? "column" : "row"}
      sx={[{
        alignItems: isLabelOnTop ? "flex-start" : "center"
      }, isLabelOnTop ? { width: "100%" } : {}]}>
      {isLabelOnTop ? (
        <SimpleFormLabel>{props.label}</SimpleFormLabel>
      ) : (
        <Typography sx={{ flex: 1 }}>{props.label}</Typography>
      )}
      <Box sx={{ flex: isLabelOnTop ? 1 : 2, width: isLabelOnTop ? "100%" : "auto" }}>
        <Autocomplete
          disableClearable
          options={props.options}
          getOptionLabel={(option: OptionsValueType) => option.label}
          value={props.value ?? props.defaultValue ?? 'Select...'}
          renderOption={(props, option) => {
            const { key, ..._props } = props;
            return (
              <li key={option.value} {..._props}>
                <Box sx={{ flex: 1 }}>{option.label}</Box>
                {option.tooltip && <Tooltip title={option.tooltip}><Info /></Tooltip>}
              </li>
            )
          }}
          renderInput={(params) => <TextField autoComplete="off" {...params} />}
          onChange={(e, newValue: OptionsValueType) => {
            settingsOptions?.updateSettingsState(props.keyid, newValue.value);
          }}
          slotProps={{
            popper: { className: "drag-cancel" }
          }}
        />
      </Box>
    </Stack>
  );
}